/**
 * useTodoLists Hook
 * Custom hook for managing todo lists state
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getTodoLists,
  createTodoList,
  updateTodoList,
  deleteTodoList,
  archiveTodoList
} from '@/services/finTasksService';

// Module-level lock to prevent race conditions when multiple components
// try to create default lists simultaneously (React Strict Mode, multiple mounts)
const defaultListCreationLock = {
  inProgress: new Map(), // Map<userId, Promise<list>>
};

export function useTodoLists() {
  const { user, loading: authLoading } = useAuth();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If auth is still loading → do nothing
    if (authLoading) {
      return;
    }

    // If user is logged out → reset state and stop
    if (!user?.uid) {
      setLists([]);
      setLoading(false);
      setError(null);
      return;
    }

    let isMounted = true;

    const fetchLists = async () => {
      setLoading(true);
      setError(null);

      try {
        let fetchedLists = await getTodoLists(user.uid);

        // If no lists are found, create a default one with race condition protection
        if (fetchedLists.length === 0) {
          try {
            // Check if another instance is already creating a default list for this user
            if (defaultListCreationLock.inProgress.has(user.uid)) {
              console.log('Default list creation already in progress, waiting...');
              const newList = await defaultListCreationLock.inProgress.get(user.uid);
              if (newList) {
                fetchedLists = [newList];
              }
            } else {
              console.log('No lists found for user. Creating a default list.');
              const defaultListData = {
                title: 'My Tasks',
                isDefault: true,
                sortOrder: 1,
              };
              
              // Create promise and store it in lock before awaiting
              const createPromise = createTodoList(user.uid, defaultListData);
              defaultListCreationLock.inProgress.set(user.uid, createPromise);
              
              try {
                const newList = await createPromise;
                if (newList) {
                  fetchedLists = [newList];
                }
              } finally {
                // Clear lock after creation completes (success or failure)
                defaultListCreationLock.inProgress.delete(user.uid);
              }
            }
          } catch (createError) {
            console.error('Error creating default list:', createError);
            defaultListCreationLock.inProgress.delete(user.uid);
            // Continue with empty list if creation fails
          }
        }

        if (isMounted) {
          setLists(fetchedLists);
        }
      } catch (err) {
        console.error('Error fetching todo lists:', err);
        if (isMounted) {
          setError(err.message || 'Failed to load lists');
          setLists([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchLists();

    return () => {
      isMounted = false;
    };
  }, [user?.uid, authLoading]);

  const refetch = useCallback(async () => {
    if (!user?.uid) return;

    setLoading(true);
    setError(null);

    try {
      const fetchedLists = await getTodoLists(user.uid);
      setLists(fetchedLists);
    } catch (err) {
      console.error('Error refetching todo lists:', err);
      setError(err.message || 'Failed to reload lists');
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  const addList = useCallback(async (listData) => {
    if (!user?.uid) {
      throw new Error('User required');
    }

    try {
      const newList = await createTodoList(user.uid, listData);
      setLists(prev => [...prev, newList]);
      return newList;
    } catch (err) {
      console.error('Error creating list:', err);
      throw err;
    }
  }, [user?.uid]);

  const editList = useCallback(async (listId, updates) => {
    if (!user?.uid) {
      throw new Error('User required');
    }

    try {
      await updateTodoList(user.uid, listId, updates);
      setLists(prev =>
        prev.map(list =>
          list.id === listId ? { ...list, ...updates } : list
        )
      );
    } catch (err) {
      console.error('Error updating list:', err);
      throw err;
    }
  }, [user?.uid]);

  const archiveList = useCallback(async (listId) => {
    if (!user?.uid) {
      throw new Error('User required');
    }

    try {
      await archiveTodoList(user.uid, listId);
      setLists(prev => prev.filter(list => list.id !== listId));
    } catch (err) {
      console.error('Error archiving list:', err);
      throw err;
    }
  }, [user?.uid]);

  const removeList = useCallback(async (listId) => {
    if (!user?.uid) {
      throw new Error('User required');
    }

    try {
      await deleteTodoList(user.uid, listId);
      setLists(prev => prev.filter(list => list.id !== listId));
    } catch (err) {
      console.error('Error deleting list:', err);
      throw err;
    }
  }, [user?.uid]);

  return {
    lists,
    loading,
    error,
    refetch,
    addList,
    editList,
    archiveList,
    removeList
  };
}

export default useTodoLists;
